import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import AICoachInsightCard from '../components/AICoachInsightCard';
import ConsistencyCard from '../components/ConsistencyCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { 
  Moon, 
  Sun, 
  Calculator, 
  Clock,
  Calendar,
  CalendarDays,
  TrendingUp,
  Award,
  Loader2,
  Star,
  Coffee,
  Dumbbell,
  AlertCircle,
  Wine,
  Frown,
  ChevronRight,
  CheckCircle2,
  Info,
  Bed,
  Sunrise,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine
} from 'recharts';

const Sleep = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  
  // Today's sleep
  const [todaySleep, setTodaySleep] = useState(null);
  
  // History
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  // Iter. 406.fr.46 — Consistency stats (score % + streaks)
  const [consistency, setConsistency] = useState(null);
  // Iter. 406.fr.45 — Period toggle for chart (week / month / year)
  const [sleepPeriod, setSleepPeriod] = useState('week');
  
  // Analysis
  const [analysis, setAnalysis] = useState(null);
  
  // Calculator
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcMode, setCalcMode] = useState('wake_time'); // wake_time or sleep_time
  const [calcTime, setCalcTime] = useState('07:00');
  const [calcResults, setCalcResults] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Log sleep modal
  const [showLogModal, setShowLogModal] = useState(false);
  // Phase 2 retroactive (2026-04-29) — null = today, else YYYY-MM-DD up to J-30
  const [logTargetDate, setLogTargetDate] = useState(null);
  const [sleepLog, setSleepLog] = useState({
    sleep_time: '23:00',
    wake_time: '07:00',
    quality: 3,
    factors: [],
    notes: ''
  });
  
  const sleepFactors = [
    { id: 'caffeine', icon: Coffee, label: language === 'fr' ? 'Caféine' : 'Caffeine' },
    { id: 'stress', icon: Frown, label: 'Stress' },
    { id: 'exercise', icon: Dumbbell, label: language === 'fr' ? 'Exercice' : 'Exercise' },
    { id: 'alcohol', icon: Wine, label: language === 'fr' ? 'Alcool' : 'Alcohol' },
    { id: 'screen', icon: AlertCircle, label: language === 'fr' ? 'Écrans' : 'Screens' },
  ];
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const daysForPeriod = sleepPeriod === 'week' ? 7 : sleepPeriod === 'month' ? 30 : 365;
      const [todayRes, historyRes, analysisRes] = await Promise.all([
        api.get('/sleep/today'),
        api.get(`/sleep/history?days=${daysForPeriod}`),
        api.get('/sleep/analysis')
      ]);
      
      if (todayRes.data.has_log) {
        setTodaySleep(todayRes.data.log);
      }
      setHistory(historyRes.data.history);
      setStats(historyRes.data.stats);
      setConsistency(historyRes.data.consistency || null);
      
      if (analysisRes.data.has_data) {
        setAnalysis(analysisRes.data);
      }
    } catch (error) {
      console.error('Error fetching sleep data:', error);
    } finally {
      setLoading(false);
    }
  }, [sleepPeriod]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Iter. 406.fr.45 — Prepare chart-friendly dataset per period.
  // - week: raw daily rows (up to 7)
  // - month: raw daily rows (up to 30)
  // - year: bucket rows by calendar month → 12 aggregated bars
  const sleepChartData = useMemo(() => {
    const valid = (history || []).filter(h => h.duration_hours);
    if (sleepPeriod !== 'year') return valid;

    // Group by YYYY-MM, average duration + score
    const monthBuckets = new Map();
    for (const row of valid) {
      const ym = (row.date || '').slice(0, 7); // "YYYY-MM"
      if (!ym) continue;
      if (!monthBuckets.has(ym)) {
        monthBuckets.set(ym, { ym, totalHours: 0, totalScore: 0, nights: 0 });
      }
      const b = monthBuckets.get(ym);
      b.totalHours += Number(row.duration_hours) || 0;
      b.totalScore += Number(row.score) || 0;
      b.nights += 1;
    }
    // Sort ascending by month and format for chart
    const sorted = [...monthBuckets.values()].sort((a, b) => a.ym.localeCompare(b.ym));
    return sorted.map((b) => {
      const [y, m] = b.ym.split('-').map(Number);
      const monthDate = new Date(y, m - 1, 1);
      const monthShort = monthDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short' });
      return {
        date: monthShort,           // 3-letter month for X-axis tick
        year_month: b.ym,
        duration_hours: Number((b.totalHours / b.nights).toFixed(1)),
        score: Math.round(b.totalScore / b.nights),
        nights: b.nights,
      };
    });
  }, [history, sleepPeriod, language]);
  
  // Calculate sleep times
  const calculateSleep = async () => {
    setCalculating(true);
    try {
      const response = await api.post('/sleep/calculate', {
        mode: calcMode,
        target_time: calcTime,
        include_fall_asleep_time: true
      });
      setCalcResults(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setCalculating(false);
    }
  };
  
  // Log sleep
  const logSleep = async () => {
    setLogging(true);
    try {
      const payload = { ...sleepLog };
      if (logTargetDate) payload.target_date = logTargetDate;
      const response = await api.post('/sleep/log', payload);
      
      setTodaySleep({
        ...sleepLog,
        score: response.data.score,
        duration_hours: response.data.duration_hours,
        cycles: response.data.cycles
      });
      
      toast.success(response.data.feedback);
      setShowLogModal(false);
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLogging(false);
    }
  };
  
  // Toggle factor
  const toggleFactor = (factorId) => {
    setSleepLog(prev => ({
      ...prev,
      factors: prev.factors.includes(factorId)
        ? prev.factors.filter(f => f !== factorId)
        : [...prev.factors, factorId]
    }));
  };
  
  // Get score color
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };
  
  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  const getGradeEmoji = (grade) => {
    switch(grade) {
      case 'A': return '🌟';
      case 'B': return '👍';
      case 'C': return '😐';
      default: return '😴';
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/20 border-2 border-purple-500 rounded-xl mb-4">
            <Moon className="w-8 h-8 text-purple-500" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-foreground">
            {t('sleep.title')}
          </h1>
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Analysez et améliorez votre sommeil' : 'Analyze and improve your sleep'}
          </p>
        </div>

        {/* Iter. 406.fr.43 — Lecture Coach YUMIX (lazy, non-bloquant) */}
        <AICoachInsightCard endpoint="/sleep/ai-summary" period="week" />
        
        {/* Today's Sleep Card — Micro-Victory glow when 7h+ achieved */}
        {todaySleep ? (() => {
          // 7h is the WHO/CDC adult lower-bound target — anything at or above
          // earns the orange-fluo signature (Yuri spec: orange = "habitude
          // gagnée"). Pre-target keeps the calm purple identity of sleep.
          const sleepHit = (todaySleep.duration_hours || 0) >= 7;
          return (
            <Card
              className={`transition-all ${
                sleepHit
                  ? 'bg-gradient-to-br from-orange-500/15 to-amber-500/5 border-amber-400/50'
                  : 'bg-gradient-to-br from-indigo-500/15 via-card to-violet-950/30 border-indigo-400/50'
              }`}
              style={{
                boxShadow: sleepHit
                  ? '0 0 18px rgba(251,146,60,0.35), 0 0 4px rgba(245,158,11,0.25)'
                  : '0 0 16px rgba(129,140,248,0.45), 0 0 5px rgba(99,102,241,0.35), inset 0 0 0 1px rgba(129,140,248,0.12)',
              }}
              data-testid="sleep-today-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Nuit dernière' : 'Last night'}</p>
                    <p className={`text-3xl font-black ${sleepHit ? 'text-amber-300' : 'text-foreground'}`}>
                      {todaySleep.duration_hours?.toFixed(1)}h
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {todaySleep.cycles} {language === 'fr' ? 'cycles' : 'cycles'}
                    </p>
                    {sleepHit && (
                      <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mt-1" data-testid="sleep-victory-tag">
                        {language === 'fr' ? '✓ Cible 7h atteinte' : '✓ 7h goal hit'}
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center ${getScoreBg(todaySleep.score)} bg-opacity-20 border-2 ${getScoreColor(todaySleep.score).replace('text-', 'border-')}`}>
                      <span className={`text-2xl font-black ${getScoreColor(todaySleep.score)}`}>
                        {todaySleep.score}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Score</p>
                  </div>
                </div>

                {/* Duration bar — orange glow when at goal */}
                <div className="mb-4">
                  <div className="h-2 rounded-full bg-secondary/50 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        sleepHit
                          ? 'bg-gradient-to-r from-orange-600 via-orange-500 to-amber-300'
                          : 'bg-purple-500'
                      }`}
                      style={{
                        width: `${Math.min(100, ((todaySleep.duration_hours || 0) / 7) * 100)}%`,
                        boxShadow: sleepHit ? '0 0 10px rgba(251,146,60,0.55), 0 0 3px rgba(245,158,11,0.45)' : 'none',
                      }}
                    />
                  </div>
                </div>

                {/* Quality stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i <= todaySleep.quality ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
                    />
                  ))}
                  <span className="text-sm text-muted-foreground ml-2">
                    {language === 'fr' ? 'Qualité ressentie' : 'Felt quality'}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Bed className="w-4 h-4" />
                    {todaySleep.sleep_time?.split('T')[1]?.substring(0, 5) || todaySleep.sleep_time}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                  <span className="flex items-center gap-1">
                    <Sunrise className="w-4 h-4" />
                    {todaySleep.wake_time?.split('T')[1]?.substring(0, 5) || todaySleep.wake_time}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })() : (
          <Card className="bg-card border-border">
            <CardContent className="pt-6 text-center">
              <Moon className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-muted-foreground mb-4">
                {language === 'fr' ? 'Pas de sommeil enregistré aujourd\'hui' : 'No sleep logged today'}
              </p>
              <Button onClick={() => setShowLogModal(true)} className="bg-purple-500 hover:bg-purple-600">
                <Moon className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Enregistrer mon sommeil' : 'Log my sleep'}
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-14 flex items-center gap-2"
            onClick={() => setShowCalculator(true)}
          >
            <Calculator className="w-5 h-5 text-primary" />
            <span className="text-sm">{language === 'fr' ? 'Calculateur' : 'Calculator'}</span>
          </Button>
          <Button
            variant="outline"
            className="h-14 flex items-center gap-2"
            onClick={() => setShowLogModal(true)}
          >
            <Bed className="w-5 h-5 text-primary" />
            <span className="text-sm">{language === 'fr' ? 'Enregistrer' : 'Log Sleep'}</span>
          </Button>
        </div>

        {/* Iter. 406.fr.46 — Sleep Consistency Card (Strava-lite KPI) */}
        {consistency && consistency.nights_tracked > 0 && (
          <ConsistencyCard
            consistency={consistency}
            theme="purple"
            title={language === 'fr' ? 'Consistance du Sommeil' : 'Sleep Consistency'}
            unitLabel={
              language === 'fr'
                ? `nuits ≥${consistency.target_hours || 7}h`
                : `nights ≥${consistency.target_hours || 7}h`
            }
            streakLabel={language === 'fr' ? 'Série actuelle' : 'Current streak'}
            bestLabel={language === 'fr' ? 'Meilleure série' : 'Best streak'}
            onTargetLabel={
              language === 'fr'
                ? `Sur les ${sleepPeriod === 'week' ? '7' : sleepPeriod === 'month' ? '30' : '365'} derniers jours.`
                : `Over the last ${sleepPeriod === 'week' ? '7' : sleepPeriod === 'month' ? '30' : '365'} days.`
            }
            fr={language === 'fr'}
            testId="sleep-consistency-card"
          />
        )}
        
        {/* Weekly History Chart — GLOW mauve signature (Iter. 386) */}
        <Card
          className="bg-card border-border relative overflow-hidden"
          style={{
            boxShadow: '0 0 0 1px rgba(192,132,252,0.30), 0 0 24px rgba(168,85,247,0.20)',
          }}
        >
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Historique' : 'History'}
              </CardTitle>
              {/* Iter. 406.fr.45 — Period toggle (Week / Month / Year) */}
              <div
                className="flex items-center gap-0.5 bg-secondary/50 rounded-lg p-0.5"
                data-testid="sleep-period-toggle"
              >
                {[
                  { key: 'week',  fr: 'Semaine', en: 'Week' },
                  { key: 'month', fr: 'Mois',    en: 'Month' },
                  { key: 'year',  fr: 'Année',   en: 'Year' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => setSleepPeriod(p.key)}
                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${
                      sleepPeriod === p.key
                        ? 'bg-purple-500/25 text-purple-200 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`sleep-period-${p.key}`}
                  >
                    {language === 'fr' ? p.fr : p.en}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className={sleepPeriod === 'year' ? 'h-56 relative' : 'h-48 relative'}>
              {/* Iter. 395 — Mode GLOW électrique : keyframes injectés inline pour
                  faire pulser doucement le drop-shadow des barres, simulant une
                  charge électrique. CSS-only, zero JS reflow. */}
              <style>{`
                @keyframes yumix-electric-pulse {
                  0%, 100% {
                    filter: drop-shadow(0 0 6px rgba(168,85,247,0.65))
                            drop-shadow(0 0 14px rgba(168,85,247,0.30));
                  }
                  50% {
                    filter: drop-shadow(0 0 10px rgba(216,180,254,0.90))
                            drop-shadow(0 0 22px rgba(168,85,247,0.55));
                  }
                }
                .yumix-electric-bar { animation: yumix-electric-pulse 2.6s ease-in-out infinite; }
                .yumix-electric-bar-warn { animation: yumix-electric-pulse-warn 2.6s ease-in-out infinite; }
                @keyframes yumix-electric-pulse-warn {
                  0%, 100% { filter: drop-shadow(0 0 5px rgba(244,114,182,0.55))
                                     drop-shadow(0 0 12px rgba(244,114,182,0.28)); }
                  50% { filter: drop-shadow(0 0 9px rgba(251,113,133,0.80))
                                drop-shadow(0 0 18px rgba(244,114,182,0.50)); }
                }
                .yumix-electric-bar-ok { animation: yumix-electric-pulse-ok 2.6s ease-in-out infinite; }
                @keyframes yumix-electric-pulse-ok {
                  0%, 100% { filter: drop-shadow(0 0 5px rgba(52,211,153,0.55))
                                     drop-shadow(0 0 12px rgba(52,211,153,0.28)); }
                  50% { filter: drop-shadow(0 0 9px rgba(110,231,183,0.80))
                                drop-shadow(0 0 18px rgba(52,211,153,0.50)); }
                }
              `}</style>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sleepChartData}>
                  {/* Mode GLOW électrique (Iter. 395) — palette signature mauve
                      avec dégradé vertical + variantes red/green pour les bars
                      sous-dormeur (<6h) et bien dormeur (≥7h). */}
                  <defs>
                    <linearGradient id="sleepBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#d8b4fe" stopOpacity={1} />
                      <stop offset="35%"  stopColor="#c084fc" stopOpacity={1} />
                      <stop offset="100%" stopColor="#6b21a8" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="sleepBarGradientWarn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#fbcfe8" stopOpacity={1} />
                      <stop offset="40%"  stopColor="#f472b6" stopOpacity={1} />
                      <stop offset="100%" stopColor="#9d174d" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="sleepBarGradientOk" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#a7f3d0" stopOpacity={1} />
                      <stop offset="40%"  stopColor="#34d399" stopOpacity={1} />
                      <stop offset="100%" stopColor="#065f46" stopOpacity={1} />
                    </linearGradient>
                    <filter id="sleepBarGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feFlood floodColor="#a855f7" floodOpacity="0.9" />
                      <feComposite in2="blur" operator="in" />
                      <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(168,85,247,0.12)" />
                  <XAxis
                    dataKey="date"
                    stroke="rgba(255,255,255,0.5)"
                    fontSize={10}
                    interval={sleepPeriod === 'week' ? 0 : 'preserveStartEnd'}
                    minTickGap={sleepPeriod === 'year' ? 30 : 12}
                    tickFormatter={(value) => {
                      if (!value) return '';
                      // Year view: bucketed by month → value is already "MMM"
                      if (sleepPeriod === 'year' && value.length <= 3) return value;
                      const date = new Date(value);
                      if (isNaN(date)) return value;
                      if (sleepPeriod === 'week') {
                        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });
                      }
                      // Month view: show day-of-month + short month
                      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short' });
                    }}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.5)"
                    fontSize={10}
                    domain={[0, 12]}
                    tickFormatter={(value) => `${value}h`}
                  />
                  {/* Cible 8h — ligne de référence électrique pulsante */}
                  <ReferenceLine
                    y={8}
                    stroke="#c084fc"
                    strokeDasharray="5 4"
                    strokeWidth={1.5}
                    style={{ filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.75))' }}
                    label={{
                      value: language === 'fr' ? '🎯 Cible 8h' : language === 'es' ? '🎯 Meta 8h' : '🎯 Target 8h',
                      position: 'right',
                      fill: '#e9d5ff',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(168,85,247,0.08)' }}
                    contentStyle={{
                      backgroundColor: 'rgba(15,3,30,0.95)',
                      border: '1px solid rgba(192,132,252,0.55)',
                      borderRadius: '10px',
                      boxShadow: '0 0 16px rgba(168,85,247,0.45), inset 0 0 8px rgba(216,180,254,0.12)',
                      backdropFilter: 'blur(8px)',
                      padding: '8px 12px',
                    }}
                    itemStyle={{ color: '#e9d5ff', fontWeight: 600 }}
                    labelStyle={{ color: '#c084fc', fontSize: 11, marginBottom: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    formatter={(value, name) => {
                      if (name === 'duration_hours') return [`${value}h`, language === 'fr' ? 'Durée' : 'Duration'];
                      if (name === 'score') return [value, 'Score'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => new Date(label).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                  />
                  <Bar
                    dataKey="duration_hours"
                    radius={[6, 6, 0, 0]}
                  >
                    {sleepChartData.map((entry, i) => {
                      const h = Number(entry.duration_hours) || 0;
                      // Sub-6h = warning state (red electric pulse).
                      // ≥7h = healthy state (green electric pulse).
                      // 6-6.99h = standard mauve electric pulse.
                      const isLow = h < 6;
                      const isOk  = h >= 7;
                      const fillId = isLow ? 'sleepBarGradientWarn'
                                   : isOk  ? 'sleepBarGradientOk'
                                           : 'sleepBarGradient';
                      const cls = isLow ? 'yumix-electric-bar-warn'
                                : isOk  ? 'yumix-electric-bar-ok'
                                        : 'yumix-electric-bar';
                      return <Cell key={`sleep-cell-${i}`} fill={`url(#${fillId})`} className={cls} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-500">{stats.average_duration || 0}h</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Moyenne' : 'Average'}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-500">{stats.average_score || 0}</p>
                <p className="text-xs text-muted-foreground">Score</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{stats.nights_tracked || 0}</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Nuits' : 'Nights'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Analysis & Tips */}
        {analysis && (
          <Card className="bg-secondary/50 border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Analyse & Conseils' : 'Analysis & Tips'}
                <span className="ml-auto text-2xl">{getGradeEmoji(analysis.sleep_grade)}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-black text-purple-500">{analysis.analysis.average_bedtime}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Coucher moyen' : 'Avg. bedtime'}</p>
                </div>
                <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-black text-yellow-500">{analysis.analysis.average_wake_time}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Réveil moyen' : 'Avg. wake'}</p>
                </div>
              </div>
              
              {/* Tips */}
              <div className="space-y-2">
                {analysis.tips.slice(0, 3).map((tip, i) => (
                  <div 
                    key={i} 
                    className={`flex items-start gap-2 p-3 rounded-lg ${
                      tip.priority === 'high' ? 'bg-red-500/10' : 
                      tip.priority === 'medium' ? 'bg-yellow-500/10' : 
                      'bg-green-500/10'
                    }`}
                  >
                    <Info className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      tip.priority === 'high' ? 'text-red-500' : 
                      tip.priority === 'medium' ? 'text-yellow-500' : 
                      'text-green-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium">{tip.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{tip.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Recommended Sleep */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              {language === 'fr' ? 'Recommandations' : 'Recommendations'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-1">
                {language === 'fr' ? 'Durée recommandée pour votre âge' : 'Recommended for your age'}
              </p>
              <p className="text-3xl font-black text-primary">7-9h</p>
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'fr' ? '(5-6 cycles de 90 min)' : '(5-6 cycles of 90 min)'}
              </p>
            </div>
            
            <ul className="space-y-2 text-sm text-muted-foreground mt-4">
              <li className="flex items-start gap-2">
                <Moon className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                {language === 'fr' ? 'Couchez-vous à la même heure chaque jour' : 'Go to bed at the same time every day'}
              </li>
              <li className="flex items-start gap-2">
                <Sun className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                {language === 'fr' ? 'Exposez-vous à la lumière naturelle le matin' : 'Get natural light exposure in the morning'}
              </li>
              <li className="flex items-start gap-2">
                <Coffee className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                {language === 'fr' ? 'Évitez la caféine après 14h' : 'Avoid caffeine after 2 PM'}
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
      
      {/* Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Calculator className="w-6 h-6 text-primary" />
                {language === 'fr' ? 'Calculateur de sommeil' : 'Sleep Calculator'}
              </CardTitle>
              <CardDescription>
                {language === 'fr' 
                  ? 'Calculez vos heures idéales basées sur les cycles de 90 minutes'
                  : 'Calculate ideal times based on 90-minute sleep cycles'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Je veux calculer...' : 'I want to calculate...'}</Label>
                <Select value={calcMode} onValueChange={setCalcMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wake_time">
                      {language === 'fr' ? 'Quand me coucher (je sais quand je veux me lever)' : 'When to go to bed (I know when to wake up)'}
                    </SelectItem>
                    <SelectItem value="sleep_time">
                      {language === 'fr' ? 'Quand me lever (je sais quand je vais me coucher)' : 'When to wake up (I know when I\'ll sleep)'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>
                  {calcMode === 'wake_time' 
                    ? (language === 'fr' ? 'Heure de réveil souhaitée' : 'Desired wake time')
                    : (language === 'fr' ? 'Heure de coucher prévue' : 'Planned bedtime')
                  }
                </Label>
                <Input
                  type="time"
                  value={calcTime}
                  onChange={(e) => setCalcTime(e.target.value)}
                  className="text-center text-lg"
                />
              </div>
              
              <Button 
                className="w-full bg-purple-500 hover:bg-purple-600"
                onClick={calculateSleep}
                disabled={calculating}
              >
                {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                {language === 'fr' ? 'Calculer' : 'Calculate'}
              </Button>
              
              {calcResults && (
                <div className="space-y-3 pt-4 border-t border-border">
                  <p className="text-sm font-medium text-center">
                    {calcMode === 'wake_time'
                      ? (language === 'fr' ? 'Heures de coucher recommandées:' : 'Recommended bedtimes:')
                      : (language === 'fr' ? 'Heures de réveil recommandées:' : 'Recommended wake times:')
                    }
                  </p>
                  {calcResults.options.map((option, i) => (
                    <div 
                      key={i}
                      className={`p-3 rounded-lg border ${
                        option.quality === 'optimal' 
                          ? 'bg-green-500/10 border-green-500/50' 
                          : 'bg-yellow-500/10 border-yellow-500/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold">
                            {calcMode === 'wake_time' ? option.bedtime : option.wake_time}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {option.cycles} {language === 'fr' ? 'cycles' : 'cycles'} • {option.sleep_hours}h
                          </p>
                        </div>
                        {option.quality === 'optimal' && (
                          <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">
                            {language === 'fr' ? 'Optimal' : 'Optimal'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <Button variant="outline" className="w-full" onClick={() => {
                setShowCalculator(false);
                setCalcResults(null);
              }}>
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Log Sleep Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Bed className="w-6 h-6 text-primary" />
                {language === 'fr' ? 'Enregistrer mon sommeil' : 'Log My Sleep'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Phase 2 retroactive — discreet date row at the top.
                  Today (default) = subtle gray. Past day = orange "Rattrapage". */}
              {(() => {
                const today = new Date();
                const ymdLocal = (d) => {
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  return `${y}-${m}-${day}`;
                };
                const todayStr = ymdLocal(today);
                const minDate = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return ymdLocal(d); })();
                const isRetro = !!logTargetDate && logTargetDate !== todayStr;
                return (
                  <div
                    className={`-mt-1 px-2.5 py-1.5 rounded-lg border transition-all ${
                      isRetro ? 'bg-orange-500/10 border-orange-500/40' : 'bg-secondary/40 border-border/50'
                    }`}
                    data-testid="sleep-log-date-row"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <CalendarDays className={`w-3.5 h-3.5 shrink-0 ${isRetro ? 'text-orange-400' : 'text-muted-foreground'}`} />
                        <span className={`text-[11px] uppercase tracking-widest font-bold truncate ${
                          isRetro ? 'text-orange-300' : 'text-muted-foreground'
                        }`}>
                          {isRetro
                            ? (language === 'fr' ? `Rattrapage · ${logTargetDate}` : `Backfill · ${logTargetDate}`)
                            : (language === 'fr' ? "Aujourd'hui" : 'Today')}
                        </span>
                      </div>
                      <input
                        type="date"
                        value={logTargetDate || todayStr}
                        min={minDate}
                        max={todayStr}
                        onChange={(e) => {
                          const v = e.target.value || todayStr;
                          setLogTargetDate(v === todayStr ? null : v);
                        }}
                        className="h-6 text-[11px] bg-transparent border-0 p-0 m-0 outline-none cursor-pointer text-muted-foreground hover:text-foreground"
                        data-testid="sleep-log-date-input"
                      />
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Moon className="w-4 h-4" />
                    {language === 'fr' ? 'Coucher' : 'Bedtime'}
                  </Label>
                  <Input
                    type="time"
                    value={sleepLog.sleep_time}
                    onChange={(e) => setSleepLog({...sleepLog, sleep_time: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Sunrise className="w-4 h-4" />
                    {language === 'fr' ? 'Réveil' : 'Wake up'}
                  </Label>
                  <Input
                    type="time"
                    value={sleepLog.wake_time}
                    onChange={(e) => setSleepLog({...sleepLog, wake_time: e.target.value})}
                  />
                </div>
              </div>
              
              {/* Quality */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Qualité ressentie' : 'Felt quality'}</Label>
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <button
                      key={i}
                      onClick={() => setSleepLog({...sleepLog, quality: i})}
                      className="p-1"
                    >
                      <Star
                        className={`w-8 h-8 transition-all ${
                          i <= sleepLog.quality 
                            ? 'text-yellow-500 fill-yellow-500' 
                            : 'text-muted-foreground hover:text-yellow-500/50'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Factors */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Facteurs qui ont affecté votre sommeil' : 'Factors that affected your sleep'}</Label>
                <div className="flex flex-wrap gap-2">
                  {sleepFactors.map(factor => (
                    <button
                      key={factor.id}
                      onClick={() => toggleFactor(factor.id)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg border transition-all ${
                        sleepLog.factors.includes(factor.id)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-secondary border-border hover:border-primary/50'
                      }`}
                    >
                      <factor.icon className="w-4 h-4" />
                      <span className="text-sm">{factor.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}</Label>
                <Input
                  placeholder={language === 'fr' ? 'Rêves, réveil nocturne...' : 'Dreams, night waking...'}
                  value={sleepLog.notes}
                  onChange={(e) => setSleepLog({...sleepLog, notes: e.target.value})}
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowLogModal(false)}>
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button 
                  className="flex-1 bg-purple-500 hover:bg-purple-600"
                  onClick={logSleep}
                  disabled={logging}
                >
                  {logging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default Sleep;
